// BasicDetails.jsx

import { useEffect, useState } from "react";
import { SweetAlertShow } from "../../../master/Master";
import { studentDetailsBasicDetails } from "../../../../api/StudentDetails";
import { formatToDate } from "../../../master/FormatToDateTime";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const DetailCard = ({
  icon,
  color,
  title,
  rows,
  loading,
  colSize = "col-lg-6",
}) => {
  const visibleRows = rows?.filter((r) => r.show !== false) ?? [];

  return (
    <div className={colSize}>
      <div
        className="p-3 bg-white rounded-4 h-100"
        style={{ border: "1px solid #eef0f4" }}
      >
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10`}
            style={{ width: 38, height: 38 }}
          >
            <i
              className={`${icon} text-${color === "primary" ? "white" : color} fs-5`}
            ></i>
          </div>
          <h6 className="mb-0 fw-semibold">{title}</h6>
        </div>

        {loading ? (
          <div className="placeholder-glow d-flex flex-column gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h="16px" />
            ))}
          </div>
        ) : visibleRows?.length ? (
          <div className="d-flex flex-column">
            {visibleRows.map((r, i) => (
              <div
                key={i}
                className="d-flex justify-content-between align-items-center py-2"
                style={{
                  borderBottom:
                    i !== visibleRows?.length - 1
                      ? "1px dashed #f0f0f0"
                      : "none",
                }}
              >
                <small className="text-secondary d-flex align-items-center gap-2">
                  {r.icon && <i className={`${r.icon} text-${color}`}></i>}
                  {r.label}
                </small>
                <small className="fw-medium text-dark text-end">
                  {r.value ?? "-"}
                </small>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <i className="ri-inbox-line d-block fs-3 mb-1"></i>
            <small>No {title.toLowerCase()} available</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BasicDetails({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsBasicDetails(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);


  const maskAadhaar = (v) => {
    if (!v) return "-";
    const clean = String(v).replace(/\s/g, "");
    if (clean?.length < 4) return v;
    return `XXXX XXXX ${clean.slice(-4)}`;
  };

  let education = [];
  try {
    education = data?.education_details
      ? JSON.parse(data.education_details)
      : [];
  } catch {
    education = [];
  }

  return (
    <div className="row g-3">
      {/* Personal Details */}
      <DetailCard
        icon="ri-user-3-line"
        color="primary"
        title="Personal Details"
        loading={loading}
        rows={[
          { icon: "ri-user-line", label: "Student Name", value: data?.name },
          {
            icon: "ri-id-card-line",
            label: "Registration No",
            value: data?.registration_no,
          },
          {
            icon: "ri-calendar-event-line",
            label: "Admission Date",
            value: formatToDate(data?.admission_date),
          },
          { icon: "ri-genderless-line", label: "Gender", value: data?.gender },
          {
            icon: "ri-cake-2-line",
            label: "Date of Birth",
            value: formatToDate(data?.dob),
          },
          {
            icon: "ri-bookmark-line",
            label: "Category",
            value: data?.category,
          },
          {
            icon: "ri-shield-check-line",
            label: "Aadhaar",
            value: maskAadhaar(data?.aadhar),
          },
          { icon: "ri-translate-2", label: "Medium", value: data?.medium },
        ]}
      />

      {/* Contact */}
      <DetailCard
        icon="ri-phone-line"
        color="success"
        title="Contact Details"
        loading={loading}
        rows={[
          {
            icon: "ri-smartphone-line",
            label: "Student Mobile",
            value: data?.phone,
          },
          {
            icon: "ri-phone-line",
            label: "Secondary Mobile",
            value: data?.secondary_mobile,
          },
          {
            icon: "ri-men-line",
            label: "Father Mobile",
            value: data?.father_mobile,
          },
          {
            icon: "ri-women-line",
            label: "Mother Mobile",
            value: data?.mother_mobile,
          },
          { icon: "ri-mail-line", label: "Email", value: data?.email },
        ]}
      />

      {/* Parent */}
      <DetailCard
        icon="ri-parent-line"
        color="warning"
        title="Parent Details"
        loading={loading}
        rows={[
          {
            icon: "ri-men-line",
            label: "Father Name",
            value: data?.father_name,
          },
          {
            icon: "ri-women-line",
            label: "Mother Name",
            value: data?.mother_name,
          },
          {
            icon: "ri-briefcase-line",
            label: "Father Occupation",
            value: data?.father_occupation,
          },
          {
            icon: "ri-user-shared-line",
            label: "Referred By",
            value: data?.referred_by,
          },
        ]}
      />

      {/* Address */}
      <DetailCard
        icon="ri-map-pin-line"
        color="danger"
        title="Address"
        loading={loading}
        rows={[
          { icon: "ri-community-line", label: "Village", value: data?.village },
          { icon: "ri-map-2-line", label: "District", value: data?.district },
          { icon: "ri-flag-line", label: "State", value: data?.state },
          { icon: "ri-hashtag", label: "Pincode", value: data?.zipcode },
          { icon: "ri-earth-line", label: "Country", value: "India" },
        ]}
      />

      {/* Academic */}
      <div className="col-lg-6">
        <div
          className="p-3 bg-white rounded-4 h-100"
          style={{ border: "1px solid #eef0f4" }}
        >
          <div className="d-flex align-items-center gap-2 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 bg-info bg-opacity-10"
              style={{ width: 38, height: 38 }}
            >
              <i className="ri-graduation-cap-line text-info fs-5"></i>
            </div>
            <h6 className="mb-0 fw-semibold">Academic Details</h6>
          </div>

          {loading ? (
            <div className="placeholder-glow d-flex flex-column gap-2">
              <Skeleton h="16px" />
              <Skeleton h="16px" />
              <Skeleton h="16px" />
            </div>
          ) : education?.length ? (
            <div className="d-flex flex-column gap-2">
              {education?.map((edu, i) => (
                <div
                  key={i}
                  className="d-flex justify-content-between align-items-center p-2 rounded-3"
                  style={{ background: "#f8f9fb" }}
                >
                  <div>
                    <div className="fw-medium">{edu?.course_name || "-"}</div>
                    <small className="text-secondary">
                      {edu?.school_name || "-"} • {edu?.year || "-"}
                    </small>
                  </div>
                  <span className="badge bg-info bg-opacity-10 text-info">
                    {edu?.score ? `${edu.score}%` : "-"}
                  </span>
                </div>
              ))}

              <div
                className="d-flex justify-content-between align-items-center pt-2 mt-1"
                style={{ borderTop: "1px dashed #f0f0f0" }}
              >
                <small className="text-secondary d-flex align-items-center gap-2">
                  <i className="ri-checkbox-circle-line text-info"></i>
                  Status
                </small>
                <span
                  className={`badge bg-${data?.status === "1" ? "success" : "secondary"} bg-opacity-10 text-${data?.status === "1" ? "success" : "secondary"}`}
                >
                  {data?.status === "1" ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="ri-inbox-line d-block fs-3 mb-1"></i>
              <small>No academic details available</small>
            </div>
          )}
        </div>
      </div>

      {/* Other */}
      <DetailCard
        icon="ri-information-line"
        color="secondary"
        title="Other Information"
        loading={loading}
        rows={[
          {
            icon: "ri-hotel-bed-line",
            label: "Hostel Status",
            value: data?.hostel_status,
          },
          {
            icon: "ri-bus-2-line",
            label: "Bus Status",
            value: data?.bus_status,
          },
          { icon: "ri-file-list-3-line", label: "PEN No", value: data?.pen_no },
          { icon: "ri-passport-line", label: "NIC ID", value: data?.nic_id },
          {
            icon: "ri-fingerprint-line",
            label: "Apaar ID",
            value: data?.apaar_id,
          },
          { icon: "ri-chat-1-line", label: "Remark", value: data?.remark },
        ]}
      />
    </div>
  );
}
